import { useCallback, useMemo, useState, Fragment } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import { Menu, Transition } from '@headlessui/react';

import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
  SavedCorpDialogue,
} from '@scale/shared/types/chat';
import { Typography, MenuItem, Tooltip, Icon } from '@scale/scaleui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import cn from 'classnames';
import { SaveDialogueModal } from './SaveDialogueModal';

type TProps = {
  dialogue: SavedCorpDialogue;
  setConfig: (config: MultiTurnInteractionConfiguration) => void;
  setResponses: (responses: IndexedInteractionStepResponse[]) => void;
  onUpdate: () => void;
  readOnly?: boolean;
};

export function CorpDialogueEntry({
  dialogue,
  setConfig,
  setResponses,
  onUpdate,
  readOnly = false,
}: TProps) {
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const isStarred = useMemo(() => dialogue.tags.includes('starred'), [dialogue.tags]);
  const loadDialogue = useCallback(() => {
    setConfig(dialogue.config);
    setResponses(dialogue.responses);
  }, [dialogue, setConfig, setResponses]);

  const toggleDialogueStar = useCallback(async () => {
    if (isStarred) {
      const newTags = dialogue.tags.filter(t => t !== 'starred');
      await API.genai.updateCorpDialogue({ dialogueId: dialogue._id, tags: newTags });
    } else {
      const newTags = [...dialogue.tags, 'starred'];
      await API.genai.updateCorpDialogue({ dialogueId: dialogue._id, tags: newTags });
    }
    onUpdate();
  }, [isStarred, dialogue, onUpdate]);

  const deleteDialogue = useCallback(async () => {
    const res = confirm('Are you sure you want to delete this dialogue?');
    if (!res) {
      return;
    }
    await API.genai.deleteCorpDialogue({ dialogueId: dialogue._id });
    onUpdate();
  }, [dialogue._id, onUpdate]);

  const updateDialogue = useCallback(() => {
    setUpdateModalOpen(true);
  }, [setUpdateModalOpen]);

  return (
    <div className="flex items-center cursor-pointer">
      {/* TODO: This component is being used as items in a list, meaning that one SaveDialogueModal will be created
       *  for each item. Maybe it could be reduced to just one modal. */}
      <SaveDialogueModal
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          onUpdate();
        }}
        config={dialogue.config}
        responses={dialogue.responses}
        dialogue={dialogue}
      />

      {dialogue.tags.includes('starred') && (
        <FontAwesomeIcon className="mr-1" size="xs" icon="star" color="#FFDF00" />
      )}

      <Menu as="div" className="relative inline-block text-left scaleui">
        {() => (
          <>
            <Menu.Button className="w-48 text-left rounded-full hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-opacity-75 flex items-center">
              <Typography.Paragraph2>{dialogue.title}</Typography.Paragraph2>
              {dialogue.description && (
                <Tooltip
                  contentClassName="w-48"
                  target={
                    <div>
                      <Icon icon="info-circle" variant={'neutral'} size={'sm'} />
                    </div>
                  }
                  side="top"
                  text={dialogue.description}
                  asChild={true}
                />
              )}
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={cn(
                  'absolute w-56 overflow-hidden origin-top-left divide-y rounded-md shadow-lg bg-neutral-0 divide-neutral-100 ring-1 ring-neutral-1000 ring-opacity-5 focus:outline-none z-50',
                  'left-0 mt-2',
                )}
              >
                <MenuItem icon={['far', 'play']} label="Load Dialogue" onClick={loadDialogue} />

                {!readOnly && (
                  <>
                    <MenuItem icon={['far', 'pen']} label="Update Title" onClick={updateDialogue} />
                    <MenuItem icon={['far', 'trash']} label="Delete" onClick={deleteDialogue} />
                    <MenuItem
                      icon={['far', 'star']}
                      label={isStarred ? 'Remove Star' : 'Add Star'}
                      onClick={toggleDialogueStar}
                    />
                  </>
                )}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
